import { formatTimeSpanNormal, useLiveCounter } from "@buzzeasy/shared-frontend-utilities";
import { Space, theme, Typography } from "antd";
import { AutoCenter, Button } from "antd-mobile";
import { FC, useCallback, useState } from "react";
import { styled } from "styled-components";
import { useGetAgentStateQuery, useGetBreakNamesQuery, useGoToAvailableMutation, useGoToBreakMutation } from "../../redux/apis/blenderApi";
import CenterContent from "../layouts/CenterContent";
import BreakList from "../presenters/BreakList";

const { Title } = Typography;

const IdleAndBreak: FC = () => {
  const { token } = theme.useToken();

  const { data: agentState, refetch: fetchAgentState } = useGetAgentStateQuery();
  const { data: breakNames } = useGetBreakNamesQuery();

  const [goToAvailable] = useGoToAvailableMutation();
  const [goToBreak] = useGoToBreakMutation();

  const [isBreakListOpen, setIsBreakListOpen] = useState(false);

  const stateTimer = useLiveCounter(agentState?.timedBreakExpiresAt ?? agentState?.stateSince ?? new Date());
  const shouldAddBreakText = !agentState?.breakName?.toLowerCase().endsWith("break");

  const imgSrc = agentState?.state === "Break" ? !agentState.breakName ? "/img/just-logged-in.png" : "/img/on-break.png" : "/img/available.png";
  const statusText = agentState?.state === "Break" ? !agentState.breakName ? "Just logged in" : `On ${agentState?.breakName} ${shouldAddBreakText ? "break" : ""}` : "Waiting for incoming task";
  const btnText = agentState?.state === "Break" ? !agentState.breakName ? "Start working" : "End break" : "Go to break";

  const handleClick = useCallback(
    () => {
      if (agentState?.state === "Break")
        void goToAvailable()
          .then(() => void fetchAgentState());
      else
        setIsBreakListOpen(true);
    },
    [agentState?.state, fetchAgentState, goToAvailable],
  );

  const handleSelectBreak = useCallback(
    (breakName: string) => {
      setIsBreakListOpen(false);
      void goToBreak(breakName)
        .then(() => void fetchAgentState()); ;
    },
    [fetchAgentState, goToBreak],
  );

  return (
    <CenterContent
      $backgroundColor={agentState?.state === "Break" ? token.colorWarning : token.colorSuccess}
      $color={token.colorText}
      $padding={token.padding}
    >
      <Space direction="vertical" size="large" align="center">
        <Space direction="vertical" size="middle" align="center">
          <AutoCenter style={{ textAlign: "center" }}>
            <Title level={2}>{statusText}</Title>
          </AutoCenter>
          <Title level={5}>{formatTimeSpanNormal(agentState?.timedBreakExpiresAt ? Math.min(0, stateTimer) : Math.max(0, stateTimer))}</Title>
        </Space>
        <ImageContainer $activeImageSrc={imgSrc}>
          <img src="/img/just-logged-in.png" alt="" />
          <img src="/img/on-break.png" alt="" />
          <img src="/img/available.png" alt="" />
        </ImageContainer>
        <div>
          <Button disabled={isBreakListOpen} onClick={handleClick}>{btnText}</Button>
          {
            isBreakListOpen &&
            <BreakList
              breakNames={breakNames ?? []}
              onSelectBreak={bn => handleSelectBreak(bn)}
              onTapOutside={() => setIsBreakListOpen(false)}
            />
          }
        </div>
      </Space>

    </CenterContent>
  );
};

export default IdleAndBreak;

const ImageContainer = styled.div<{ $activeImageSrc: string }>`
width: 256px;
height: 256px;
position: relative;

& > img {
  position: absolute;
  left: 0;
  top: 0;
  width: 256px;
  height: 256px;
  object-fit: contain;

  animation-duration: ${p => p.theme.motionDurationFast};
  animation-timing-function: ${p => p.theme.motionEaseInOut};
  animation-fill-mode: both;

  animation-name: img-disappear;

  &[src="${p => p.$activeImageSrc}"] {
    animation-name: img-appear;
  }

  @keyframes img-appear {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes img-disappear {
    from { opacity: 1; }
    to { opacity: 0; }
  }
}
`;
